/**
 * File classification for the shared file viewer — the ONE extension list.
 *
 * Neither the app nor the server contract carries a MIME type anywhere (the
 * richest server shape, `EuapiEventFile`, is `{ name, extension, size, is_image,
 * url }`), so "what is this file?" is always sniffed from the extension. Before
 * this module existed that sniff lived in six places with three different
 * membership sets (models.ts, services/api/events.ts, utils/sanitize-html.ts,
 * NoteDetailModal.vue, useComposerAttachments.ts) — a HEIC upload was an image
 * in three of them and a document in the others. New code classifies here.
 */

/** Lower-case, no dot. Extensions an `<img>` can be expected to decode. */
export const IMAGE_EXTENSIONS: ReadonlySet<string> = new Set([
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'avif',
  'bmp',
  'svg',
  'heic',
  'heif',
]);

/**
 * HEIC/HEIF decode in an `<img>` only on Apple platforms (Safari / WKWebView).
 * Chrome and the Android System WebView cannot, and the backend's S3 image
 * optimizer accepts only JPG/PNG/WebP, so a HEIC upload is served untouched.
 * Treating it as an image elsewhere produces an `<img>` error that no retry can
 * fix — which the viewer would then misreport as an expired link.
 */
const APPLE_EXTENSIONS: ReadonlySet<string> = new Set(['heic', 'heif']);

/**
 * Whether the app runs on an Apple platform — iPhone, iPad, iPod, or a Mac
 * (iPadOS reports itself as `Macintosh`) — read from the user agent. It decides
 * what an `<img>` can decode (HEIC, above) and how a file picker's `accept` is
 * written (given MIME types, the iOS picker converts a HEIC photo to JPEG —
 * `services/api/leave-attachments.ts`).
 */
export function isApplePlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod|Macintosh/.test(navigator.userAgent);
}

/** What the viewer can do with a file. */
export type ViewerKind = 'image' | 'pdf' | 'other';

/** Lower-case extension without the dot, or `''` when the name has none. */
export function extensionOf(name: string): string {
  const clean = name.trim();
  const dot = clean.lastIndexOf('.');
  if (dot <= 0 || dot === clean.length - 1) return '';
  // Strip a trailing query/hash in case a URL was passed instead of a name.
  return clean.slice(dot + 1).replace(/[?#].*$/, '').toLowerCase();
}

/** The file name at the end of a URL path, or `''`. */
export function fileNameFromUrl(url: string): string {
  if (!url) return '';
  try {
    return decodeURIComponent(new URL(url).pathname.split('/').filter(Boolean).pop() ?? '');
  } catch {
    return '';
  }
}

/** e.g. "2.4 MB". `''` when the size is unknown — never "0 B". */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const rounded = unit === 0 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded} ${units[unit] ?? 'B'}`;
}

/** Whether the extension is one an `<img>` can render on THIS platform. */
export function isImageExtension(extension: string): boolean {
  const ext = extension.toLowerCase();
  if (!IMAGE_EXTENSIONS.has(ext)) return false;
  if (APPLE_EXTENSIONS.has(ext)) return isApplePlatform();
  return true;
}

/**
 * Classify a file for the viewer.
 *
 * `isImage` is the server's own answer when it gave one (events, todo comments,
 * notes send `is_image`); it is trusted over the extension EXCEPT for HEIC/HEIF,
 * where the server's "yes" describes the file, not what this WebView can decode.
 */
export function viewerKind(extension: string, isImage?: boolean): ViewerKind {
  const ext = extension.toLowerCase();
  if (ext === 'pdf') return 'pdf';
  if (APPLE_EXTENSIONS.has(ext)) return isApplePlatform() ? 'image' : 'other';
  if (isImage === true) return 'image';
  if (isImage === false) return 'other';
  return isImageExtension(ext) ? 'image' : 'other';
}
